import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Button,
  ChevronDownIcon,
  CogIcon,
  GripVerticalIcon,
  Label,
  LoadingSpinner,
  MultiSelect,
  NumberInput,
  PlusIcon,
  SaveIcon,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import {
  createEFormRelation,
  deleteEFormRelation,
  saveEFormDraftSchema,
  saveEFormPrintLayout,
  updateEFormDefinition,
} from '../../services/mutations';
import { useApprovalTemplates } from '../approvals/useData';
import type {
  EFormAccessSettings,
  EFormFieldSchema,
  EFormRole,
  EFormSchema,
  EFormWorkflowAction,
  EFormWorkflowCondition,
  EFormWorkflowRule,
} from './schema';
import { flattenSchemaFields, isEFormDisplayField } from './schema';
import { useFormStudioActions } from './FormStudioContext';
import {
  type EFormDefinition,
  type EFormPrintBlock,
  type EFormPrintFooterContent,
  type EFormPrintFooterSlots,
  type EFormPrintLayout,
  type EFormPrintLayoutDocument,
  type EFormPrintMargins,
  type EFormPrintPageSize,
  type EFormPrintTemplate,
  getDraftVersion,
  getPublishedVersion,
  useEFormDefinition,
  useEFormPrintLayout,
  useEFormRelations,
  useEFormReportCatalog,
  usePublishedEForms,
} from './useData';
import {
  asPrintDocument,
  createPrintTemplate,
  EMPTY_PRINT_LAYOUT,
  printModeLabel,
  serializePrintDocument,
} from './printLayoutModel';

type UserRow = {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
};

type DepartmentRow = {
  id: number;
  name?: string;
  departmentName?: string;
  code?: string;
};

const ROLES: EFormRole[] = ['owner', 'editor', 'filler', 'viewer'];
const FIELD_ACCESS = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'read', label: 'Read only' },
  { value: 'write', label: 'Can edit' },
];
const CONDITION_OPERATORS: Array<{ value: EFormWorkflowCondition['operator']; label: string }> = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Does not equal' },
  { value: 'in', label: 'Is one of' },
  { value: 'contains', label: 'Contains' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
  { value: 'gte', label: 'Greater than or equal' },
  { value: 'lte', label: 'Less than or equal' },
];
const EVENTS: Array<{ value: EFormWorkflowAction['event']; label: string }> = [
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
];
const DEFAULT_MARGINS: EFormPrintMargins = { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 };
const MARGIN_PRESETS: Array<{ id: string; label: string; margins: EFormPrintMargins }> = [
  { id: 'normal', label: 'Normal', margins: { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 } },
  { id: 'narrow', label: 'Narrow', margins: { top: 12.7, right: 12.7, bottom: 12.7, left: 12.7 } },
  { id: 'moderate', label: 'Moderate', margins: { top: 25.4, right: 19.1, bottom: 25.4, left: 19.1 } },
  { id: 'wide', label: 'Wide', margins: { top: 25.4, right: 50.8, bottom: 25.4, left: 50.8 } },
  { id: 'office2003', label: 'Office 2003 default', margins: { top: 25.4, right: 31.8, bottom: 25.4, left: 31.8 } },
];

const DEFAULT_FOOTER_SLOTS: Required<EFormPrintFooterSlots> = {
  left: 'none',
  center: 'page',
  right: 'datetime',
};

const FOOTER_CONTENTS: Array<{ id: EFormPrintFooterContent; label: string; hint: string }> = [
  { id: 'none', label: 'None', hint: 'Leave this side empty' },
  { id: 'datetime', label: 'Printed date and time', hint: 'Stamped when the PDF is created' },
  { id: 'page', label: 'Page 1 of N', hint: 'Page number and total pages' },
  { id: 'text', label: 'Custom text', hint: 'Same text on every page' },
];

const DEFAULT_LAYOUT: EFormPrintLayout = { ...EMPTY_PRINT_LAYOUT };

const DEFAULT_PRINT_DOC: EFormPrintLayoutDocument = {
  schemaVersion: 1,
  defaultTemplateId: 'default',
  templates: [{ ...DEFAULT_LAYOUT, id: 'default', name: 'Default' }],
};

function normalizeMargins(value?: Partial<EFormPrintMargins> | null): EFormPrintMargins {
  const clamp = (n: unknown) => {
    const parsed = Number(n);
    if (!Number.isFinite(parsed)) return DEFAULT_MARGINS.top;
    return Math.min(80, Math.max(5, Math.round(parsed * 10) / 10));
  };
  return {
    top: clamp(value?.top),
    right: clamp(value?.right),
    bottom: clamp(value?.bottom),
    left: clamp(value?.left),
  };
}

function sameMargins(a: EFormPrintMargins, b: EFormPrintMargins): boolean {
  return (['top', 'right', 'bottom', 'left'] as const).every((side) => Math.abs(a[side] - b[side]) < 0.15);
}

function marginPresetId(margins: EFormPrintMargins): string {
  return MARGIN_PRESETS.find((preset) => sameMargins(margins, preset.margins))?.id ?? 'custom';
}

function formatCm(mm: number): string {
  return `${(mm / 10).toFixed(2)} cm`;
}

function formatPrintDateTime(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function normalizeFooterSlots(layout: Pick<EFormPrintLayout, 'footer' | 'footerSlots'>): Required<EFormPrintFooterSlots> {
  if (layout.footerSlots) {
    return {
      left: layout.footerSlots.left ?? 'none',
      center: layout.footerSlots.center ?? 'none',
      right: layout.footerSlots.right ?? 'none',
    };
  }
  if (layout.footer?.trim()) {
    return { left: 'text', center: 'none', right: 'none' };
  }
  return { ...DEFAULT_FOOTER_SLOTS };
}

function footerSlotPreview(content: EFormPrintFooterContent | undefined, customText: string): string {
  if (content === 'datetime') return formatPrintDateTime();
  if (content === 'page') return 'Page 1 of N';
  if (content === 'text') return customText.trim();
  return '';
}

function footerContentLabel(content: EFormPrintFooterContent | undefined): string {
  return FOOTER_CONTENTS.find((item) => item.id === content)?.label ?? 'None';
}

const ORIENTATIONS: Array<{ id: 'portrait' | 'landscape'; label: string }> = [
  { id: 'portrait', label: 'Portrait' },
  { id: 'landscape', label: 'Landscape' },
];

const PAPER_SIZES: Array<{ id: EFormPrintPageSize; label: string; widthMm: number; heightMm: number }> = [
  { id: 'LETTER', label: 'Letter', widthMm: 215.9, heightMm: 279.4 },
  { id: 'LEGAL', label: 'Legal', widthMm: 215.9, heightMm: 355.6 },
  { id: 'EXECUTIVE', label: 'Executive', widthMm: 184.2, heightMm: 266.7 },
  { id: 'A3', label: 'A3', widthMm: 297, heightMm: 420 },
  { id: 'A4', label: 'A4', widthMm: 210, heightMm: 297 },
  { id: 'A5', label: 'A5', widthMm: 148, heightMm: 210 },
  { id: 'B4', label: 'B4 (JIS)', widthMm: 257, heightMm: 364 },
  { id: 'B5', label: 'B5 (JIS)', widthMm: 182, heightMm: 257 },
  { id: 'TABLOID', label: '11 × 17', widthMm: 279.4, heightMm: 431.8 },
];

function paperSizeLabel(id: string): { label: string; detail: string } {
  const paper = PAPER_SIZES.find((item) => item.id === id) ?? PAPER_SIZES.find((item) => item.id === 'A4')!;
  return {
    label: paper.label,
    detail: `${(paper.widthMm / 10).toFixed(2)} × ${(paper.heightMm / 10).toFixed(2)} cm`,
  };
}

function PageThumb({ landscape = false, className }: { landscape?: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'block shrink-0 rounded-[1px] border border-slate-400 bg-white',
        landscape ? 'h-5 w-7' : 'h-7 w-5',
        className,
      )}
      aria-hidden
    />
  );
}

function MarginThumb({ margins, className }: { margins: EFormPrintMargins; className?: string }) {
  const inset = (mm: number) => `${Math.max(2, Math.min(11, mm / 5))}px`;
  return (
    <span
      className={cn('relative block h-8 w-6 shrink-0 rounded-[1px] border border-sky-500 bg-white', className)}
      aria-hidden
    >
      <span
        className="absolute border border-sky-400/80"
        style={{
          top: inset(margins.top),
          right: inset(margins.right),
          bottom: inset(margins.bottom),
          left: inset(margins.left),
        }}
      />
    </span>
  );
}

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const value = data as { data?: unknown; items?: unknown };
    if (Array.isArray(value.data)) return value.data as T[];
    if (Array.isArray(value.items)) return value.items as T[];
  }
  return [];
}

function id(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function cloneSchema(schema: EFormSchema): EFormSchema {
  return JSON.parse(JSON.stringify(schema)) as EFormSchema;
}

function assignment(
  config: EFormAccessSettings,
  role: EFormRole,
): { userIds: number[]; departmentIds: number[] } {
  const row = config.assignments?.find((item) => item.role === role);
  return { userIds: row?.userIds ?? [], departmentIds: row?.departmentIds ?? [] };
}

function userLabel(user: UserRow): string {
  return (
    user.userDisplayName ||
    [user.userFirstName, user.userLastName].filter(Boolean).join(' ') ||
    user.userEmail ||
    `User ${user.id}`
  );
}

function fieldLabel(field: EFormFieldSchema): string {
  return field.label || field.key;
}

const Section: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, description, children, className }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border', colors.border, colors.surface, className)}>
      <div className={cn('border-b px-5 py-4', colors.border)}>
        <h2 className={cn('text-sm font-semibold', colors.text)}>{title}</h2>
        {description ? <p className={cn('mt-1 text-xs', colors.textMuted)}>{description}</p> : null}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
};

const PrintPageSetup: React.FC<{
  layout: EFormPrintLayout;
  onChange: (patch: Partial<EFormPrintLayout>) => void;
}> = ({ layout, onChange }) => {
  const { colors } = useTheme();
  const margins = normalizeMargins(layout.margins);
  const presetId = marginPresetId(margins);
  const selectedPreset = MARGIN_PRESETS.find((preset) => preset.id === presetId);
  const [openMenu, setOpenMenu] = useState<
    'orientation' | 'paper' | 'margins' | 'footer-left' | 'footer-center' | 'footer-right' | null
  >(null);
  const footerSlots = normalizeFooterSlots(layout);
  const usesCustomFooter = (['left', 'center', 'right'] as const).some((side) => footerSlots[side] === 'text');
  const patchFooterSlot = (side: keyof EFormPrintFooterSlots, content: EFormPrintFooterContent) => {
    onChange({ footerSlots: { ...footerSlots, [side]: content } });
  };
  const [customOpen, setCustomOpen] = useState(presetId === 'custom');
  const menuRef = useRef<HTMLDivElement>(null);
  const paper = paperSizeLabel(layout.pageSize);

  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [openMenu]);

  const patchMargin = (side: keyof EFormPrintMargins, cm: number | null) => {
    onChange({
      margins: {
        ...margins,
        [side]: cm == null ? DEFAULT_MARGINS[side] : Math.min(80, Math.max(5, cm * 10)),
      },
    });
  };

  const triggerClass = cn(
    'flex min-h-10 w-full items-center gap-2 rounded-xl border px-3 py-1.5 text-left text-sm',
    colors.border,
    colors.surface,
    colors.interactiveHover,
  );
  const menuClass =
    'absolute z-30 mt-1 w-full overflow-hidden rounded-lg border bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900';

  return (
    <div ref={menuRef} className="space-y-4">
      <div className="relative">
        <Label>Orientation</Label>
        <button
          type="button"
          className={triggerClass}
          aria-expanded={openMenu === 'orientation'}
          aria-haspopup="listbox"
          onClick={() => setOpenMenu((current) => (current === 'orientation' ? null : 'orientation'))}
        >
          <PageThumb landscape={layout.orientation === 'landscape'} />
          <span className="min-w-0 flex-1 truncate font-medium">
            {layout.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
          </span>
          <ChevronDownIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
        </button>
        {openMenu === 'orientation' ? (
          <div role="listbox" className={menuClass}>
            {ORIENTATIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={layout.orientation === option.id}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-left text-sm',
                  layout.orientation === option.id
                    ? 'bg-blue-50 dark:bg-blue-950/40'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                )}
                onClick={() => {
                  onChange({ orientation: option.id });
                  setOpenMenu(null);
                }}
              >
                <PageThumb landscape={option.id === 'landscape'} />
                <span className="font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="relative">
        <Label>Paper size</Label>
        <button
          type="button"
          className={triggerClass}
          aria-expanded={openMenu === 'paper'}
          aria-haspopup="listbox"
          onClick={() => setOpenMenu((current) => (current === 'paper' ? null : 'paper'))}
        >
          <PageThumb />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{paper.label}</span>
            <span className={cn('block truncate text-xs', colors.textMuted)}>{paper.detail}</span>
          </span>
          <ChevronDownIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
        </button>
        {openMenu === 'paper' ? (
          <div role="listbox" className={cn(menuClass, 'max-h-72 overflow-y-auto')}>
            {PAPER_SIZES.map((option) => {
              const active = layout.pageSize === option.id;
              const detail = `${(option.widthMm / 10).toFixed(2)} × ${(option.heightMm / 10).toFixed(2)} cm`;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={cn(
                    'flex w-full items-start gap-3 px-3 py-2 text-left text-sm',
                    active ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                  )}
                  onClick={() => {
                    onChange({ pageSize: option.id });
                    setOpenMenu(null);
                  }}
                >
                  <PageThumb className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-medium">{option.label}</span>
                    <span className={cn('block text-xs', colors.textMuted)}>{detail}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="relative">
        <Label>Margins</Label>
        <button
          type="button"
          className={triggerClass}
          aria-expanded={openMenu === 'margins'}
          aria-haspopup="listbox"
          onClick={() => setOpenMenu((current) => (current === 'margins' ? null : 'margins'))}
        >
          <MarginThumb margins={margins} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{selectedPreset?.label ?? 'Custom margins'}</span>
            <span className={cn('block truncate text-xs', colors.textMuted)}>
              Top: {formatCm(margins.top)} · Bottom: {formatCm(margins.bottom)}
            </span>
          </span>
          <ChevronDownIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
        </button>
        {openMenu === 'margins' ? (
          <div role="listbox" className={cn(menuClass, 'w-[22rem] max-w-[calc(100vw-2rem)]')}>
            {MARGIN_PRESETS.map((preset) => {
              const active = preset.id === presetId;
              return (
                <button
                  key={preset.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={cn(
                    'flex w-full items-start gap-3 px-3 py-2 text-left text-sm',
                    active ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                  )}
                  onClick={() => {
                    onChange({ margins: preset.margins });
                    setCustomOpen(false);
                    setOpenMenu(null);
                  }}
                >
                  <MarginThumb margins={preset.margins} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-medium">{preset.label}</span>
                    <span className={cn('block text-xs', colors.textMuted)}>
                      Top: {formatCm(preset.margins.top)} · Bottom: {formatCm(preset.margins.bottom)}
                    </span>
                    <span className={cn('block text-xs', colors.textMuted)}>
                      Left: {formatCm(preset.margins.left)} · Right: {formatCm(preset.margins.right)}
                    </span>
                  </span>
                </button>
              );
            })}
            <div className={cn('mt-1 border-t', colors.border)}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() => {
                  setCustomOpen(true);
                  setOpenMenu(null);
                }}
              >
                <MarginThumb margins={margins} />
                Custom margins…
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {customOpen || presetId === 'custom' ? (
        <div className="grid grid-cols-2 gap-3">
          {([
            ['top', 'Top'],
            ['bottom', 'Bottom'],
            ['left', 'Left'],
            ['right', 'Right'],
          ] as const).map(([side, label]) => (
            <div key={side}>
              <Label className="!mb-1">{label}</Label>
              <NumberInput
                value={Number((margins[side] / 10).toFixed(2))}
                min={0.5}
                max={8}
                step={0.01}
                allowDecimals
                showClearButton={false}
                suffix={<span className={cn('text-xs', colors.textMuted)}>cm</span>}
                onChange={(event) => patchMargin(side, event.target.value)}
              />
            </div>
          ))}
        </div>
      ) : null}
      <div>
        <Label>Document title</Label>
        <TextInput value={layout.title ?? ''} onChange={(event) => onChange({ title: event.target.value })} />
      </div>
      <div>
        <Label>Header</Label>
        <Textarea value={layout.header ?? ''} onChange={(event) => onChange({ header: event.target.value })} rows={2} />
      </div>
      <div className="space-y-2">
        <Label>Footer</Label>
        <p className={cn('text-xs', colors.textMuted)}>Choose what prints on each side of the page.</p>
        <div className="grid grid-cols-3 gap-2">
          {([
            ['left', 'Left', 'footer-left'],
            ['center', 'Center', 'footer-center'],
            ['right', 'Right', 'footer-right'],
          ] as const).map(([side, label, menuId]) => (
            <div key={side} className="relative min-w-0">
              <Label className="!mb-1">{label}</Label>
              <button
                type="button"
                className={cn(triggerClass, 'px-2')}
                aria-expanded={openMenu === menuId}
                aria-haspopup="listbox"
                onClick={() => setOpenMenu((current) => (current === menuId ? null : menuId))}
              >
                <span className="min-w-0 flex-1 truncate font-medium">{footerContentLabel(footerSlots[side])}</span>
                <ChevronDownIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} />
              </button>
              {openMenu === menuId ? (
                <div role="listbox" className={cn(menuClass, 'w-[16rem]')}>
                  {FOOTER_CONTENTS.map((option) => {
                    const active = footerSlots[side] === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={cn(
                          'flex w-full flex-col items-start px-3 py-2 text-left text-sm',
                          active ? 'bg-blue-50 dark:bg-blue-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                        )}
                        onClick={() => {
                          patchFooterSlot(side, option.id);
                          setOpenMenu(null);
                        }}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className={cn('text-xs', colors.textMuted)}>{option.hint}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
        {usesCustomFooter ? (
          <div>
            <Label className="!mb-1">Custom text</Label>
            <TextInput
              value={layout.footer ?? ''}
              onChange={(event) => onChange({ footer: event.target.value })}
              placeholder="Text printed on the selected side"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PrintFooterBar: React.FC<{ layout: EFormPrintLayout }> = ({ layout }) => {
  const slots = normalizeFooterSlots(layout);
  const custom = layout.footer ?? '';
  const left = footerSlotPreview(slots.left, custom);
  const center = footerSlotPreview(slots.center, custom);
  const right = footerSlotPreview(slots.right, custom);
  if (!left && !center && !right) return null;
  return (
    <div className="mt-auto grid grid-cols-3 gap-2 border-t border-slate-200 pt-2 text-[10px] leading-4 text-slate-500">
      <span className="min-w-0 truncate text-left">{left}</span>
      <span className="min-w-0 truncate text-center">{center}</span>
      <span className="min-w-0 truncate text-right">{right}</span>
    </div>
  );
};

const PrintPaper: React.FC<{ layout: EFormPrintLayout; children: React.ReactNode }> = ({ layout, children }) => {
  const margins = normalizeMargins(layout.margins);
  const landscape = layout.orientation === 'landscape';
  const paper = PAPER_SIZES.find((item) => item.id === layout.pageSize) ?? PAPER_SIZES.find((item) => item.id === 'A4')!;
  const widthMm = landscape ? paper.heightMm : paper.widthMm;
  const heightMm = landscape ? paper.widthMm : paper.heightMm;
  return (
    <div
      className="bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.12),0_10px_28px_rgba(0,0,0,0.12)]"
      style={{
        width: landscape ? 'min(100%, 52rem)' : 'min(100%, 36rem)',
        aspectRatio: `${widthMm} / ${heightMm}`,
        paddingTop: `${margins.top}mm`,
        paddingRight: `${margins.right}mm`,
        paddingBottom: `${margins.bottom}mm`,
        paddingLeft: `${margins.left}mm`,
      }}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden">{children}</div>
    </div>
  );
};

const PrintDesigner: React.FC<{
  settings: React.ReactNode;
  children: React.ReactNode;
  layout: EFormPrintLayout;
}> = ({ settings, children, layout }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
      <div className="grid xl:grid-cols-[22rem_minmax(0,1fr)]">
        <aside className={cn('space-y-5 border-b p-4 xl:border-b-0 xl:border-r', colors.border, colors.surface)}>
          {settings}
        </aside>
        <div className="flex min-h-[40rem] items-start justify-center bg-[#ededed] p-8 dark:bg-slate-950">
          <PrintPaper layout={layout}>{children}</PrintPaper>
        </div>
      </div>
    </div>
  );
};

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('rounded-lg border border-dashed px-4 py-8 text-center text-sm', colors.border, colors.textMuted)}>
      {children}
    </div>
  );
};

export type PlatformSettingsSection = 'access' | 'fields' | 'workflow' | 'relations' | 'print';

const PlatformSettingsPage: React.FC<{
  embedded?: boolean;
  section?: PlatformSettingsSection;
}> = ({ embedded = false, section = 'access' }) => {
  const { id: idParam } = useParams<{ id: string }>();
  const definitionId = Number(idParam);
  const validId = Number.isInteger(definitionId) && definitionId > 0;
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { setTitleOverride } = useTopbar();
  const definition = useEFormDefinition(validId ? definitionId : 0);
  const relations = useEFormRelations(validId ? definitionId : null);
  const printData = useEFormPrintLayout(validId ? definitionId : null);
  const publishedForms = usePublishedEForms('', 1, 500);
  const { data: templates = [] } = useApprovalTemplates();
  const { data: usersRaw } = useSWR('/users?limit=500', fetcher);
  const { data: departmentsRaw } = useSWR('/departments?limit=500', fetcher);

  const [schema, setSchema] = useState<EFormSchema | null>(null);
  const [access, setAccess] = useState<EFormAccessSettings>({ assignments: [], defaultRole: 'none' });
  const [printDoc, setPrintDoc] = useState<EFormPrintLayoutDocument>(DEFAULT_PRINT_DOC);
  const [selectedTemplateId, setSelectedTemplateId] = useState('default');
  const [saving, setSaving] = useState(false);
  const [loadedDefinitionId, setLoadedDefinitionId] = useState<number | null>(null);
  const [loadedPrintVersion, setLoadedPrintVersion] = useState<number | null>(null);
  const savedSchema = useRef('');
  const savedAccess = useRef('');
  const savedLayout = useRef('');
  const [showRelationForm, setShowRelationForm] = useState(false);
  const [showAccessDetails, setShowAccessDetails] = useState(false);

  const [relationName, setRelationName] = useState('');
  const [relationTargetId, setRelationTargetId] = useState<number | ''>('');
  const [relationSourceField, setRelationSourceField] = useState('');
  const [relationTargetField, setRelationTargetField] = useState('');
  const [relationBusy, setRelationBusy] = useState(false);
  const targetCatalog = useEFormReportCatalog(
    typeof relationTargetId === 'number' ? relationTargetId : null,
  );

  const users = useMemo(() => asList<UserRow>(usersRaw), [usersRaw]);
  const departments = useMemo(() => asList<DepartmentRow>(departmentsRaw), [departmentsRaw]);
  const fields = useMemo(
    () => (schema ? flattenSchemaFields(schema).filter((field) => !isEFormDisplayField(field)) : []),
    [schema],
  );

  useEffect(() => {
    setTitleOverride(`${definition.data?.name ?? 'E-form'} · Platform settings`);
    return () => setTitleOverride(undefined);
  }, [definition.data?.name, setTitleOverride]);

  useEffect(() => {
    if (!definition.data || loadedDefinitionId === definition.data.id) return;
    const version = getDraftVersion(definition.data) ?? getPublishedVersion(definition.data);
    if (!version?.schemaJson) return;
    setSchema(cloneSchema(version.schemaJson));
    setAccess(definition.data.accessConfig ?? { assignments: [], defaultRole: 'none' });
    savedSchema.current = JSON.stringify(version.schemaJson);
    savedAccess.current = JSON.stringify(
      definition.data.accessConfig ?? { assignments: [], defaultRole: 'none' },
    );
    setLoadedDefinitionId(definition.data.id);
  }, [definition.data, loadedDefinitionId]);

  useEffect(() => {
    if (!printData.data || loadedPrintVersion === printData.data.id) return;
    const normalized = asPrintDocument(
      printData.data.printLayoutJson,
      Boolean(definition.data?.sourceFileName),
    );
    setPrintDoc(normalized);
    setSelectedTemplateId(normalized.defaultTemplateId || normalized.templates[0]?.id || 'default');
    savedLayout.current = JSON.stringify(serializePrintDocument(normalized));
    setLoadedPrintVersion(printData.data.id);
  }, [definition.data?.sourceFileName, printData.data, loadedPrintVersion]);

  const layout: EFormPrintTemplate =
    printDoc.templates.find((template) => template.id === selectedTemplateId) ??
    printDoc.templates[0] ??
    DEFAULT_PRINT_DOC.templates[0];

  const setLayout = (updater: EFormPrintLayout | ((current: EFormPrintTemplate) => EFormPrintLayout)) => {
    setPrintDoc((document) => {
      const current =
        document.templates.find((template) => template.id === selectedTemplateId) ??
        document.templates[0];
      if (!current) return document;
      const nextLayout = typeof updater === 'function' ? updater(current) : updater;
      return {
        ...document,
        templates: document.templates.map((template) =>
          template.id === current.id ? { ...template, ...nextLayout, id: template.id, name: template.name } : template,
        ),
      };
    });
  };

  const patchSelectedTemplate = (patch: Partial<EFormPrintTemplate>) => {
    setPrintDoc((document) => ({
      ...document,
      templates: document.templates.map((template) =>
        template.id === selectedTemplateId ? { ...template, ...patch } : template,
      ),
    }));
  };

  const addPrintout = () => {
    const template = createPrintTemplate(printDoc.templates, { outputMode: 'designed' });
    setPrintDoc((document) => ({
      ...document,
      templates: [...document.templates, template],
    }));
    setSelectedTemplateId(template.id);
  };

  const removePrintout = (templateId: string) => {
    if (printDoc.templates.length <= 1) return;
    setPrintDoc((document) => {
      const templates = document.templates.filter((template) => template.id !== templateId);
      const defaultTemplateId =
        document.defaultTemplateId === templateId ? templates[0].id : document.defaultTemplateId;
      return { ...document, templates, defaultTemplateId };
    });
    if (selectedTemplateId === templateId) {
      const next = printDoc.templates.find((template) => template.id !== templateId);
      if (next) setSelectedTemplateId(next.id);
    }
  };

  const setDefaultPrintout = (templateId: string) => {
    setPrintDoc((document) => ({ ...document, defaultTemplateId: templateId }));
  };

  const patchAssignment = (
    role: EFormRole,
    key: 'userIds' | 'departmentIds',
    values: Array<string | number>,
  ) => {
    setAccess((current) => {
      const assignments = ROLES.map((itemRole) => {
        const currentRow = assignment(current, itemRole);
        return {
          role: itemRole,
          ...currentRow,
          ...(itemRole === role ? { [key]: values.map(Number) } : {}),
        };
      }).filter((row) => row.userIds.length || row.departmentIds.length);
      return { ...current, assignments };
    });
  };

  const updateFieldAccess = (fieldId: string, role: EFormRole, value: string) => {
    setSchema((current) => {
      if (!current) return current;
      return {
        ...current,
        sections: current.sections.map((section) => ({
          ...section,
          fields: section.fields.map((field) =>
            field.id === fieldId
              ? {
                  ...field,
                  roleAccess: {
                    ...field.roleAccess,
                    [role]: value as 'hidden' | 'read' | 'write',
                  },
                }
              : field,
          ),
        })),
      };
    });
  };

  const setRules = (rules: EFormWorkflowRule[]) => {
    setSchema((current) =>
      current
        ? { ...current, settings: { ...current.settings, workflowRules: rules } }
        : current,
    );
  };

  const setActions = (actions: EFormWorkflowAction[]) => {
    setSchema((current) =>
      current
        ? { ...current, settings: { ...current.settings, workflowActions: actions } }
        : current,
    );
  };

  const rules = schema?.settings?.workflowRules ?? [];
  const actions = schema?.settings?.workflowActions ?? [];

  const save = async () => {
    if (!schema || !validId) return;
    setSaving(true);
    try {
      await updateEFormDefinition(definitionId, { accessConfig: access });
      await Promise.all([
        saveEFormDraftSchema(definitionId, schema),
        saveEFormPrintLayout(definitionId, serializePrintDocument(printDoc) as unknown as Record<string, unknown>),
      ]);
      await Promise.all([definition.mutate(), printData.mutate()]);
      savedSchema.current = JSON.stringify(schema);
      savedAccess.current = JSON.stringify(access);
      savedLayout.current = JSON.stringify(serializePrintDocument(printDoc));
      showSuccess('Platform settings saved');
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not save platform settings');
    } finally {
      setSaving(false);
    }
  };

  const addRule = () => {
    setRules([
      ...rules,
      {
        id: id('rule'),
        label: `Workflow rule ${rules.length + 1}`,
        templateCode: '',
        priority: rules.length + 1,
        conditions: [{ fieldKey: fields[0]?.key ?? '', operator: 'eq', value: '' }],
      },
    ]);
  };

  const patchRule = (ruleId: string, patch: Partial<EFormWorkflowRule>) => {
    setRules(rules.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule)));
  };

  const patchCondition = (
    rule: EFormWorkflowRule,
    index: number,
    patch: Partial<EFormWorkflowCondition>,
  ) => {
    patchRule(rule.id, {
      conditions: rule.conditions.map((condition, i) =>
        i === index ? { ...condition, ...patch } : condition,
      ),
    });
  };

  const addAction = () => {
    setActions([
      ...actions,
      {
        id: id('action'),
        event: 'approved',
        type: 'set_field',
        fieldKey: fields[0]?.key ?? '',
        value: '',
      },
    ]);
  };

  const patchAction = (actionId: string, patch: Partial<EFormWorkflowAction>) => {
    setActions(actions.map((action) => (action.id === actionId ? { ...action, ...patch } : action)));
  };

  const addPrintBlock = (kind: EFormPrintBlock['kind']) => {
    setLayout((current) => ({
      ...current,
      blocks: [
        ...current.blocks,
        {
          id: id('block'),
          kind,
          ...(kind === 'title' ? { text: 'Section title' } : {}),
          ...(kind === 'text' ? { text: 'Text' } : {}),
          ...(kind === 'field' ? { fieldKey: fields[0]?.key ?? '', label: fields[0]?.label } : {}),
        },
      ],
    }));
  };

  const patchBlock = (blockId: string, patch: Partial<EFormPrintBlock>) => {
    setLayout((current) => ({
      ...current,
      blocks: current.blocks.map((block) => (block.id === blockId ? { ...block, ...patch } : block)),
    }));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    setLayout((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.blocks.length) return current;
      const blocks = [...current.blocks];
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      return { ...current, blocks };
    });
  };

  const createRelation = async () => {
    if (
      !validId ||
      typeof relationTargetId !== 'number' ||
      !relationName.trim() ||
      !relationSourceField ||
      !relationTargetField
    ) {
      showError('Complete the relation name, target form, and both join fields');
      return;
    }
    setRelationBusy(true);
    try {
      await createEFormRelation(definitionId, {
        name: relationName.trim(),
        targetDefinitionId: relationTargetId,
        sourceFieldId: relationSourceField,
        targetFieldId: relationTargetField,
        isActive: true,
      });
      await relations.mutate();
      setRelationName('');
      setRelationTargetId('');
      setRelationSourceField('');
      setRelationTargetField('');
      setShowRelationForm(false);
      showSuccess('Relation created');
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not create relation');
    } finally {
      setRelationBusy(false);
    }
  };

  const removeRelation = async (relationId: number) => {
    setRelationBusy(true);
    try {
      await deleteEFormRelation(definitionId, relationId);
      await relations.mutate();
      showSuccess('Relation deleted');
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Could not delete relation');
    } finally {
      setRelationBusy(false);
    }
  };

  const dirty =
    Boolean(schema) &&
    (JSON.stringify(schema) !== savedSchema.current ||
      JSON.stringify(access) !== savedAccess.current ||
      JSON.stringify(serializePrintDocument(printDoc)) !== savedLayout.current);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  useFormStudioActions(
    embedded
      ? {
          save,
          saving,
          dirty,
          statusLabel: dirty ? 'Unpublished changes' : 'Draft saved',
        }
      : null,
  );
  const sectionCopy: Record<PlatformSettingsSection, { title: string; description: string; status: string }> = {
    access: {
      title: 'Access roles',
      description: 'Choose a simple access preset, then add specific users or departments only when needed.',
      status: access.defaultRole === 'filler' ? 'Open' : access.assignments?.length ? 'Restricted' : 'Not configured',
    },
    fields: {
      title: 'Field permissions',
      description: 'Hide sensitive fields or make them read-only for each form role.',
      status: fields.some((field) => field.roleAccess && Object.keys(field.roleAccess).length) ? 'Configured' : 'Using defaults',
    },
    workflow: {
      title: 'Workflow',
      description: 'Start with the default approval, then add routing rules and outcome actions if the process needs them.',
      status: schema?.settings?.requireApproval ? 'Enabled' : 'Not enabled',
    },
    relations: {
      title: 'Data & relations',
      description: 'Connect matching fields across forms so reports can use related data.',
      status: relations.data.length ? `${relations.data.length} configured` : 'Not configured',
    },
    print: {
      title: 'Print output',
      description: 'Create one or more named printouts — table, single record, or source template.',
      status: `${printDoc.templates.length} printout${printDoc.templates.length === 1 ? '' : 's'}`,
    },
  };

  if (!validId) {
    return <div className="p-6 text-sm text-red-600">Invalid form definition.</div>;
  }

  if (definition.isLoading || !schema) {
    return (
      <div className="flex min-h-[20rem] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={cn('min-h-full', colors.background, colors.text)}>
      {!embedded ? <div className={cn('sticky top-0 z-20 border-b px-4 py-3 sm:px-6', colors.border, colors.surface)}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="min-w-0">
            <div className={cn('flex items-center gap-2 text-xs', colors.textMuted)}>
              <Link className="hover:underline" to={`/e-forms/builder/${definitionId}`}>Builder</Link>
              <span>/</span>
              <span>Platform settings</span>
            </div>
            <h1 className="mt-1 truncate text-lg font-semibold">{definition.data?.name}</h1>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
              Back
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={saving}>
              <SaveIcon className="mr-1.5 h-4 w-4" />
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </div>
      </div> : null}

      <main className={embedded ? '' : 'mx-auto max-w-7xl p-4 sm:p-6'}>
        {embedded && section !== 'access' && section !== 'fields' && section !== 'print' ? (
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">{sectionCopy[section].title}</h2>
              <p className={cn('mt-1 max-w-3xl text-sm', colors.textMuted)}>{sectionCopy[section].description}</p>
            </div>
            <span className={cn('rounded-full border px-2.5 py-1 text-xs font-medium', colors.border)}>
              {sectionCopy[section].status}
            </span>
          </div>
        ) : null}
        <Tabs defaultValue="access" value={embedded ? section : undefined}>
          {!embedded ? <TabsList variant="pill" className="mb-5 overflow-x-auto">
            <TabsTrigger value="access" appearance="pill">Access</TabsTrigger>
            <TabsTrigger value="fields" appearance="pill">Field access</TabsTrigger>
            <TabsTrigger value="workflow" appearance="pill">Workflow</TabsTrigger>
            <TabsTrigger value="relations" appearance="pill">Relations</TabsTrigger>
            <TabsTrigger value="print" appearance="pill">Print layout</TabsTrigger>
          </TabsList> : null}

          <TabsContent value="access" className="mt-0">
            <Section
              title="Access roles"
              description="Assign users and departments to effective form roles. Empty assignments leave that role unrestricted."
            >
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setAccess((current) => ({ ...current, defaultRole: 'filler' }))}
                    className={cn(
                      'rounded-xl border p-4 text-left transition-colors',
                      access.defaultRole === 'filler' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : colors.border,
                    )}
                  >
                    <div className="text-sm font-semibold">Open to permitted users</div>
                    <div className={cn('mt-1 text-xs', colors.textMuted)}>Anyone with module access can fill the form. Add exceptions only if needed.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAccess((current) => ({ ...current, defaultRole: 'none' }));
                      setShowAccessDetails(true);
                    }}
                    className={cn(
                      'rounded-xl border p-4 text-left transition-colors',
                      access.defaultRole !== 'filler' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : colors.border,
                    )}
                  >
                    <div className="text-sm font-semibold">Restricted</div>
                    <div className={cn('mt-1 text-xs', colors.textMuted)}>Only assigned users and departments receive a role.</div>
                  </button>
                </div>
                <div className="max-w-sm">
                  <Label>Default role</Label>
                  <Select
                    value={access.defaultRole ?? 'none'}
                    onChange={(event) =>
                      setAccess((current) => ({
                        ...current,
                        defaultRole: event.target.value as 'filler' | 'viewer' | 'none',
                      }))
                    }
                    options={[
                      { value: 'none', label: 'No default access' },
                      { value: 'filler', label: 'Filler' },
                      { value: 'viewer', label: 'Viewer' },
                    ]}
                    showClearButton={false}
                  />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowAccessDetails((current) => !current)}>
                  {showAccessDetails ? 'Hide role assignments' : 'Assign users and departments'}
                </Button>
                {showAccessDetails || Boolean(access.assignments?.length) ? <div className="grid gap-4 lg:grid-cols-2">
                  {ROLES.map((role) => {
                    const row = assignment(access, role);
                    return (
                      <div key={role} className={cn('rounded-lg border p-4', colors.border, colors.headerBg)}>
                        <h3 className="mb-3 text-sm font-semibold capitalize">{role}</h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <Label>Users</Label>
                            <MultiSelect
                              options={users.map((user) => ({ value: user.id, label: userLabel(user) }))}
                              value={row.userIds}
                              onChange={(value) => patchAssignment(role, 'userIds', value)}
                              placeholder="Select users"
                            />
                          </div>
                          <div>
                            <Label>Departments</Label>
                            <MultiSelect
                              options={departments.map((department) => ({
                                value: department.id,
                                label: department.departmentName || department.name || department.code || `Department ${department.id}`,
                              }))}
                              value={row.departmentIds}
                              onChange={(value) => patchAssignment(role, 'departmentIds', value)}
                              placeholder="Select departments"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div> : null}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="fields" className="mt-0">
            <Section
              title="Field role access"
              description="Set visibility and edit rights for each role. These rules are stored with the draft form schema."
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className={cn('border-b text-left text-xs', colors.border, colors.textMuted)}>
                      <th className="pb-3 pr-4 font-semibold">Field</th>
                      {ROLES.map((role) => <th key={role} className="px-2 pb-3 font-semibold capitalize">{role}</th>)}
                    </tr>
                  </thead>
                  <tbody className={cn('divide-y', colors.divide)}>
                    {fields.map((field) => (
                      <tr key={field.id}>
                        <td className="py-3 pr-4">
                          <div className="font-medium">{fieldLabel(field)}</div>
                          <div className={cn('font-mono text-[11px]', colors.textMuted)}>{field.key}</div>
                        </td>
                        {ROLES.map((role) => (
                          <td key={role} className="px-2 py-3">
                            <Select
                              size="sm"
                              value={field.roleAccess?.[role] ?? 'write'}
                              onChange={(event) => updateFieldAccess(field.id, role, event.target.value)}
                              options={FIELD_ACCESS}
                              showClearButton={false}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="workflow" className="mt-0 space-y-5">
            <Section
              title="Default approval"
              description="Turn on approval first. Every submission uses this template unless a routing rule below matches."
            >
              <div className="flex flex-wrap items-end gap-3">
                <Button
                  variant={schema.settings?.requireApproval ? 'secondary' : 'default'}
                  onClick={() =>
                    setSchema((current) =>
                      current
                        ? {
                            ...current,
                            settings: {
                              ...current.settings,
                              requireApproval: !current.settings?.requireApproval,
                              ...(!current.settings?.requireApproval
                                ? {}
                                : { approvalTemplateCode: undefined }),
                            },
                          }
                        : current,
                    )
                  }
                >
                  {schema.settings?.requireApproval ? 'Disable approval' : 'Enable approval'}
                </Button>
                {schema.settings?.requireApproval ? (
                  <div className="min-w-[18rem] flex-1">
                    <Label>Default approval template</Label>
                    <Select
                      value={schema.settings?.approvalTemplateCode ?? ''}
                      onChange={(event) =>
                        setSchema((current) =>
                          current
                            ? {
                                ...current,
                                settings: {
                                  ...current.settings,
                                  requireApproval: true,
                                  approvalTemplateCode: event.target.value || undefined,
                                },
                              }
                            : current,
                        )
                      }
                      options={templates.map((template) => ({ value: template.code, label: template.name }))}
                      placeholder="Select a template"
                    />
                  </div>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>Submissions will complete without an approval request.</p>
                )}
              </div>
            </Section>
            {schema.settings?.requireApproval ? (
              <>
            <Section
              title="Approval routing rules"
              description="When [field] [operator] [value], use [template]. Rules are evaluated by priority."
            >
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className={cn('rounded-lg border p-4', colors.border, colors.headerBg)}>
                    <div className="grid gap-3 md:grid-cols-[1fr_1fr_7rem_auto]">
                      <TextInput value={rule.label} onChange={(event) => patchRule(rule.id, { label: event.target.value })} placeholder="Rule label" />
                      <Select
                        value={rule.templateCode}
                        onChange={(event) => patchRule(rule.id, { templateCode: event.target.value })}
                        options={templates.map((template) => ({ value: template.code, label: template.name }))}
                        placeholder="Approval template"
                      />
                      <TextInput
                        type="number"
                        value={String(rule.priority ?? 0)}
                        onChange={(event) => patchRule(rule.id, { priority: Number(event.target.value) })}
                        placeholder="Priority"
                      />
                      <Button variant="ghost" onClick={() => setRules(rules.filter((item) => item.id !== rule.id))} aria-label="Delete rule">
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-3 space-y-2">
                      {rule.conditions.map((condition, index) => {
                        const noValue = condition.operator === 'empty' || condition.operator === 'notEmpty';
                        return (
                          <div key={`${rule.id}-${index}`} className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                            <Select
                              value={condition.fieldKey}
                              onChange={(event) => patchCondition(rule, index, { fieldKey: event.target.value })}
                              options={fields.map((field) => ({ value: field.key, label: fieldLabel(field) }))}
                              placeholder="Field"
                            />
                            <Select
                              value={condition.operator}
                              onChange={(event) =>
                                patchCondition(rule, index, {
                                  operator: event.target.value as EFormWorkflowCondition['operator'],
                                })
                              }
                              options={CONDITION_OPERATORS}
                              showClearButton={false}
                            />
                            <TextInput
                              value={noValue ? '' : String(condition.value ?? '')}
                              disabled={noValue}
                              onChange={(event) => patchCondition(rule, index, { value: event.target.value })}
                              placeholder={condition.operator === 'in' ? 'Comma-separated values' : 'Value'}
                            />
                            <Button
                              variant="ghost"
                              onClick={() =>
                                patchRule(rule.id, {
                                  conditions: rule.conditions.filter((_, i) => i !== index),
                                })
                              }
                              aria-label="Delete condition"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          patchRule(rule.id, {
                            conditions: [
                              ...rule.conditions,
                              { fieldKey: fields[0]?.key ?? '', operator: 'eq', value: '' },
                            ],
                          })
                        }
                      >
                        <PlusIcon className="mr-1.5 h-4 w-4" /> Add condition
                      </Button>
                    </div>
                  </div>
                ))}
                {!rules.length ? <EmptyHint>No workflow routing rules yet.</EmptyHint> : null}
                <Button variant="secondary" onClick={addRule}>
                  <PlusIcon className="mr-1.5 h-4 w-4" /> Add rule
                </Button>
              </div>
            </Section>

            <Section
              title="Post-outcome actions"
              description="Update a field, notify users, or call an HTTPS webhook when approval reaches an outcome."
            >
              <div className="space-y-3">
                {actions.map((action) => (
                  <div key={action.id} className={cn('grid gap-3 rounded-lg border p-4 lg:grid-cols-[10rem_10rem_1fr_1fr_auto]', colors.border, colors.headerBg)}>
                    <Select
                      value={action.event}
                      onChange={(event) => patchAction(action.id, { event: event.target.value as EFormWorkflowAction['event'] })}
                      options={EVENTS}
                      showClearButton={false}
                    />
                    <Select
                      value={action.type}
                      onChange={(event) => patchAction(action.id, { type: event.target.value as EFormWorkflowAction['type'] })}
                      options={[
                        { value: 'set_field', label: 'Set field' },
                        { value: 'notification', label: 'Notify users' },
                        { value: 'webhook', label: 'Webhook' },
                      ]}
                      showClearButton={false}
                    />
                    {action.type === 'set_field' ? (
                      <>
                        <Select
                          value={action.fieldKey ?? ''}
                          onChange={(event) => patchAction(action.id, { fieldKey: event.target.value })}
                          options={fields.map((field) => ({ value: field.key, label: fieldLabel(field) }))}
                          placeholder="Field"
                        />
                        <TextInput
                          value={String(action.value ?? '')}
                          onChange={(event) => patchAction(action.id, { value: event.target.value })}
                          placeholder="New value"
                        />
                      </>
                    ) : action.type === 'notification' ? (
                      <>
                        <TextInput
                          value={(action.recipients ?? []).join(', ')}
                          onChange={(event) =>
                            patchAction(action.id, {
                              recipients: event.target.value
                                .split(',')
                                .map((value) => value.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="user@example.com, team@example.com"
                        />
                        <TextInput
                          value={String(action.value ?? '')}
                          onChange={(event) => patchAction(action.id, { value: event.target.value })}
                          placeholder="Notification message"
                        />
                      </>
                    ) : (
                      <div className="lg:col-span-2">
                        <TextInput
                          type="url"
                          value={action.url ?? ''}
                          onChange={(event) => patchAction(action.id, { url: event.target.value })}
                          placeholder="https://example.com/webhook"
                        />
                      </div>
                    )}
                    <Button variant="ghost" onClick={() => setActions(actions.filter((item) => item.id !== action.id))} aria-label="Delete action">
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {!actions.length ? <EmptyHint>No post-outcome actions yet.</EmptyHint> : null}
                <Button variant="secondary" onClick={addAction}>
                  <PlusIcon className="mr-1.5 h-4 w-4" /> Add action
                </Button>
              </div>
            </Section>
              </>
            ) : (
              <EmptyHint>Enable approval to configure routing rules and outcome actions.</EmptyHint>
            )}
          </TabsContent>

          <TabsContent value="relations" className="mt-0 space-y-5">
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setShowRelationForm((current) => !current)}>
                <PlusIcon className="mr-1.5 h-4 w-4" />
                {showRelationForm ? 'Cancel' : 'Add relation'}
              </Button>
            </div>
            {showRelationForm ? (
            <Section
              title="Create relation"
              description="Link this form to another published form through matching field values."
            >
              <div className="grid gap-3 lg:grid-cols-2">
                <div>
                  <Label>Name</Label>
                  <TextInput value={relationName} onChange={(event) => setRelationName(event.target.value)} placeholder="e.g. Purchase order" />
                </div>
                <div>
                  <Label>Target form</Label>
                  <Select
                    value={relationTargetId}
                    onChange={(event) => {
                      setRelationTargetId(Number(event.target.value));
                      setRelationTargetField('');
                    }}
                    options={publishedForms.data
                      .filter((form) => form.id !== definitionId)
                      .map((form) => ({ value: form.id, label: `${form.name} (${form.code})` }))}
                    placeholder="Select target form"
                  />
                </div>
                <div>
                  <Label>Field on this form</Label>
                  <Select
                    value={relationSourceField}
                    onChange={(event) => setRelationSourceField(event.target.value)}
                    options={fields.map((field) => ({ value: field.id, label: `${fieldLabel(field)} · ${field.key}` }))}
                    placeholder="Select join field"
                  />
                </div>
                <div>
                  <Label>Field on target form</Label>
                  <Select
                    value={relationTargetField}
                    onChange={(event) => setRelationTargetField(event.target.value)}
                    options={(targetCatalog.data?.fields ?? []).map((field) => ({
                      value: field.fieldId,
                      label: `${field.label} · ${field.fieldKey}`,
                    }))}
                    placeholder={targetCatalog.isLoading ? 'Loading fields…' : 'Select join field'}
                    disabled={!relationTargetId || targetCatalog.isLoading}
                  />
                </div>
              </div>
              <Button className="mt-4" onClick={() => void createRelation()} disabled={relationBusy}>
                <PlusIcon className="mr-1.5 h-4 w-4" /> Create relation
              </Button>
            </Section>
            ) : null}
            <Section title="Existing relations">
              <div className="space-y-2">
                {relations.data.map((relation) => (
                  <div key={relation.id} className={cn('flex items-center justify-between gap-4 rounded-lg border p-4', colors.border, colors.headerBg)}>
                    <div className="min-w-0">
                      <div className="font-medium">{relation.name}</div>
                      <div className={cn('mt-1 truncate text-xs', colors.textMuted)}>
                        {relation.sourceFieldId} → {relation.targetDefinition?.name ?? relation.targetDefinitionId} / {relation.targetFieldId}
                      </div>
                    </div>
                    <Button variant="ghost" onClick={() => void removeRelation(relation.id)} disabled={relationBusy} aria-label="Delete relation">
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {!relations.data.length ? <EmptyHint>No relations configured.</EmptyHint> : null}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="print" className="mt-0">
            <Section
              title="Printouts"
              description="Each form can have several named printouts. Select one to edit its layout."
              className="mb-5"
            >
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Button variant="secondary" size="sm" onClick={addPrintout}>
                  <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add printout
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {printDoc.templates.map((template) => {
                  const active = template.id === layout.id;
                  const isDefault = template.id === printDoc.defaultTemplateId;
                  return (
                    <div
                      key={template.id}
                      className={cn(
                        'relative rounded-xl border p-3 transition-colors',
                        active
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                          : colors.border,
                      )}
                    >
                      <button
                        type="button"
                        className="absolute right-2 top-2 rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/40"
                        disabled={printDoc.templates.length <= 1}
                        onClick={() => removePrintout(template.id)}
                        title="Delete printout"
                        aria-label={`Delete ${template.name}`}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="w-full pr-8 text-left"
                        onClick={() => setSelectedTemplateId(template.id)}
                      >
                        {active ? (
                          <TextInput
                            size="sm"
                            value={template.name}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => patchSelectedTemplate({ name: event.target.value })}
                            className="mb-1 font-semibold"
                          />
                        ) : (
                          <div className="truncate text-sm font-semibold">{template.name}</div>
                        )}
                        <div className={cn('mt-1 text-xs', colors.textMuted)}>
                          {printModeLabel(template.outputMode)}
                          {isDefault ? ' · Default' : ''}
                        </div>
                      </button>
                      {!isDefault ? (
                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDefaultPrintout(template.id)}
                          >
                            Set default
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </Section>
            <div className="mb-3 text-sm font-medium">
              Editing: <span className="font-semibold">{layout.name}</span>
            </div>
            <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  id: 'table' as const,
                  title: 'Table output',
                  description: 'Print many submissions as a table. Choose the columns from print blocks.',
                  disabled: false,
                },
                {
                  id: 'designed' as const,
                  title: 'Single record',
                  description: 'Print one filled form with titles, text and fields in document order.',
                  disabled: false,
                },
                {
                  id: 'source' as const,
                  title: 'Use source template',
                  description: definition.data?.sourceFileName
                    ? definition.data.sourceFileName
                    : 'Upload a PDF or DOCX in Form design first.',
                  disabled: !definition.data?.sourceFileName,
                },
                {
                  id: 'none' as const,
                  title: 'No print output',
                  description: 'Keep this printout unused — it will not appear in print actions.',
                  disabled: false,
                },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => setLayout((current) => ({ ...current, outputMode: option.id }))}
                  className={cn(
                    'rounded-xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    layout.outputMode === option.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : colors.border,
                  )}
                >
                  <div className="text-sm font-semibold">{option.title}</div>
                  <div className={cn('mt-1 text-xs', colors.textMuted)}>{option.description}</div>
                </button>
              ))}
            </div>
            {layout.outputMode === 'table' ? (
            <PrintDesigner
              layout={layout}
              settings={
                <>
                  <PrintPageSetup
                    layout={layout}
                    onChange={(patch) => setLayout((current) => ({ ...current, ...patch }))}
                  />
                  <div>
                    <Label>Table columns</Label>
                    <p className={cn('mb-2 text-xs', colors.textMuted)}>Fields print left to right.</p>
                    <Button variant="secondary" size="sm" onClick={() => addPrintBlock('field')}>
                      <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add column
                    </Button>
                    <div className="mt-3 space-y-2">
                      {layout.blocks.filter((block) => block.kind === 'field').map((block) => {
                        const index = layout.blocks.findIndex((item) => item.id === block.id);
                        return (
                          <div key={block.id} className={cn('grid items-center gap-2 rounded-lg border p-2 sm:grid-cols-[auto_1fr_auto]', colors.border, colors.headerBg)}>
                            <GripVerticalIcon className={cn('h-4 w-4', colors.textMuted)} />
                            <Select
                              size="sm"
                              value={block.fieldKey ?? ''}
                              onChange={(event) => {
                                const selected = fields.find((field) => field.key === event.target.value);
                                patchBlock(block.id, { fieldKey: event.target.value, label: selected?.label });
                              }}
                              options={fields.map((field) => ({ value: field.key, label: fieldLabel(field) }))}
                              placeholder="Select field"
                            />
                            <div className="flex">
                              <Button variant="ghost" size="sm" onClick={() => moveBlock(index, -1)} disabled={index <= 0} aria-label="Move column up">↑</Button>
                              <Button variant="ghost" size="sm" onClick={() => moveBlock(index, 1)} disabled={index === layout.blocks.length - 1} aria-label="Move column down">↓</Button>
                              <Button variant="ghost" size="sm" onClick={() => setLayout((current) => ({ ...current, blocks: current.blocks.filter((item) => item.id !== block.id) }))} aria-label="Delete column">
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              }
            >
              {layout.header ? <div className="mb-3 border-b pb-2 text-xs text-slate-500">{layout.header}</div> : null}
              {layout.title ? <h2 className="mb-3 text-lg font-bold">{layout.title}</h2> : null}
              <div className="min-w-0 flex-1 overflow-auto">
                {layout.blocks.some((block) => block.kind === 'field') ? (
                  <table className="w-full border-collapse text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {layout.blocks.filter((block) => block.kind === 'field').map((block) => (
                          <th key={block.id} className="px-2 py-1.5 font-semibold">
                            {block.label || fields.find((field) => field.key === block.fieldKey)?.label || block.fieldKey || 'Field'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((row) => (
                        <tr key={row} className="border-b border-slate-100">
                          {layout.blocks.filter((block) => block.kind === 'field').map((block) => (
                            <td key={block.id} className="px-2 py-1.5 text-slate-400">Sample {row}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-slate-400">Add columns to preview the table.</p>
                )}
              </div>
              <PrintFooterBar layout={layout} />
            </PrintDesigner>
            ) : layout.outputMode === 'designed' ? (
            <PrintDesigner
              layout={layout}
              settings={
                <>
                  <PrintPageSetup
                    layout={layout}
                    onChange={(patch) => setLayout((current) => ({ ...current, ...patch }))}
                  />
                  <div>
                    <Label>Print blocks</Label>
                    <div className="mb-3 mt-1 flex flex-wrap gap-2">
                      {(['title', 'text', 'field', 'divider', 'spacer'] as const).map((kind) => (
                        <Button key={kind} variant="secondary" size="sm" onClick={() => addPrintBlock(kind)}>
                          <PlusIcon className="mr-1 h-3.5 w-3.5" /> <span className="capitalize">{kind}</span>
                        </Button>
                      ))}
                    </div>
                    <div className="space-y-2">
                      {layout.blocks.map((block, index) => (
                        <div key={block.id} className={cn('grid items-center gap-2 rounded-lg border p-2 sm:grid-cols-[auto_4.5rem_1fr_auto]', colors.border, colors.headerBg)}>
                          <GripVerticalIcon className={cn('h-4 w-4', colors.textMuted)} />
                          <span className="text-[10px] font-semibold uppercase">{block.kind}</span>
                          {block.kind === 'field' ? (
                            <Select
                              size="sm"
                              value={block.fieldKey ?? ''}
                              onChange={(event) => {
                                const selected = fields.find((field) => field.key === event.target.value);
                                patchBlock(block.id, { fieldKey: event.target.value, label: selected?.label });
                              }}
                              options={fields.map((field) => ({ value: field.key, label: fieldLabel(field) }))}
                              placeholder="Select field"
                            />
                          ) : block.kind === 'title' || block.kind === 'text' ? (
                            <TextInput size="sm" value={block.text ?? ''} onChange={(event) => patchBlock(block.id, { text: event.target.value })} />
                          ) : (
                            <span className={cn('text-xs', colors.textMuted)}>{block.kind === 'divider' ? 'Horizontal rule' : 'Vertical space'}</span>
                          )}
                          <div className="flex">
                            <Button variant="ghost" size="sm" onClick={() => moveBlock(index, -1)} disabled={index === 0} aria-label="Move block up">↑</Button>
                            <Button variant="ghost" size="sm" onClick={() => moveBlock(index, 1)} disabled={index === layout.blocks.length - 1} aria-label="Move block down">↓</Button>
                            <Button variant="ghost" size="sm" onClick={() => setLayout((current) => ({ ...current, blocks: current.blocks.filter((item) => item.id !== block.id) }))} aria-label="Delete block">
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              }
            >
              {layout.header ? <div className="mb-5 border-b pb-2 text-xs text-slate-500">{layout.header}</div> : null}
              {layout.title ? <h2 className="mb-5 text-xl font-bold">{layout.title}</h2> : null}
              <div className="flex-1 space-y-3 overflow-auto">
                {layout.blocks.map((block) => {
                  if (block.kind === 'title') return <h3 key={block.id} className="text-base font-semibold">{block.text || 'Title'}</h3>;
                  if (block.kind === 'text') return <p key={block.id} className="whitespace-pre-wrap text-sm">{block.text || 'Text'}</p>;
                  if (block.kind === 'divider') return <hr key={block.id} className="border-slate-300" />;
                  if (block.kind === 'spacer') return <div key={block.id} className="h-8" />;
                  return (
                    <div key={block.id} className="grid grid-cols-[8rem_1fr] gap-3 border-b border-slate-100 py-1.5 text-sm">
                      <span className="font-medium">{block.label || fields.find((field) => field.key === block.fieldKey)?.label || block.fieldKey || 'Field'}</span>
                      <span className="text-slate-400">Sample value</span>
                    </div>
                  );
                })}
              </div>
              <PrintFooterBar layout={layout} />
            </PrintDesigner>
            ) : (
              <EmptyHint>
                {layout.outputMode === 'source'
                  ? 'The original uploaded template will be used. No additional layout is required.'
                  : 'Print and PDF actions will stay hidden for this form.'}
              </EmptyHint>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PlatformSettingsPage;
